/**
 * @fileoverview The canvas header strip — surface tabs (Columns & Layout /
 *             Filters / Grouping) + Help / Tour / "+ Add field" actions.
 * @module ui-admin/components/ReportCanvasBuilder/ReportCanvasToolbar
 *
 * Responsibilities: - Render the report-canvas surface tab strip with counts,
 *                     plus the help, tour, and add-field buttons (stateless —
 *                     all actions are callbacks into the parent canvas).
 * Dependencies:     - React, lucide-react, ./reportDefaults (surface type),
 *                     ./reportTourSteps (tour anchor).
 * Architecture:     - Presentation-only, controlled.
 * Notes:            - The add-field button is the tour anchor for the palette step.
 */

import React from 'react';
import { Columns3, Filter, Sigma, HelpCircle, Compass, PanelLeftOpen } from 'lucide-react';
import type { ReportCanvasSurface } from './reportDefaults';
import { REPORT_TOUR_TARGET } from './reportTourSteps';

/**
 * @contract ReportCanvasToolbarProps
 * Purpose: Props for the canvas header strip.
 * Fields:
 * - surface / onSurfaceChange: controlled surface tab.
 * - columnCount / filterCount / parameterCount: counts shown on the tabs.
 * - mode: 'edit' shows Add field + surface tabs; 'run' hides authoring chrome.
 * - onOpenHelp / onStartTour / onAddField: action callbacks.
 */
export interface ReportCanvasToolbarProps {
  surface: ReportCanvasSurface;
  onSurfaceChange: (surface: ReportCanvasSurface) => void;
  columnCount: number;
  filterCount: number;
  parameterCount: number;
  mode: 'edit' | 'run';
  onOpenHelp: () => void;
  onStartTour: () => void;
  onAddField: () => void;
}

/**
 * @component ReportCanvasToolbar
 * Purpose:     Canvas header strip — surface tabs + help/tour/add-field.
 * Props:       ReportCanvasToolbarProps.
 * Behavior:    Stateless; mode='run' renders only the help button (no authoring).
 * Data Source: none (controlled).
 */
export function ReportCanvasToolbar({
  surface, onSurfaceChange, columnCount, filterCount, parameterCount, mode,
  onOpenHelp, onStartTour, onAddField,
}: ReportCanvasToolbarProps) {
  const tabs: Array<{ id: ReportCanvasSurface; label: string; icon: React.ReactNode; count?: number }> = [
    { id: 'columns', label: 'Columns & Layout', icon: <Columns3 className="h-3.5 w-3.5" />, count: columnCount },
    { id: 'filters', label: 'Filters', icon: <Filter className="h-3.5 w-3.5" />, count: filterCount + parameterCount },
    { id: 'grouping', label: 'Grouping', icon: <Sigma className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2">
      {mode === 'edit' && (
        <div className="flex items-center gap-1 rounded-md bg-gray-100 p-0.5" role="tablist" aria-label="Canvas surface" data-tour={REPORT_TOUR_TARGET.surfaceTabs}>
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={surface === t.id}
              data-testid={`surface-tab-${t.id}`}
              onClick={() => onSurfaceChange(t.id)}
              className={`flex items-center gap-1.5 rounded-sm px-3 py-1 text-sm font-medium ${
                surface === t.id ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.icon} {t.label}{t.count !== undefined && t.count > 0 ? ` (${t.count})` : ''}
            </button>
          ))}
        </div>
      )}

      <span className="flex-1" />

      <button type="button" data-testid="open-builder-help" onClick={onOpenHelp}
        className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50">
        <HelpCircle className="h-3.5 w-3.5" /> Help
      </button>
      {mode === 'edit' && (
        <>
          <button type="button" data-testid="start-builder-tour" onClick={onStartTour}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50">
            <Compass className="h-3.5 w-3.5" /> Tour
          </button>
          <button
            type="button"
            data-testid="open-palette"
            data-tour={REPORT_TOUR_TARGET.addField}
            onClick={onAddField}
            className="flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1 text-sm font-medium text-white hover:bg-orange-700"
          >
            <PanelLeftOpen className="h-3.5 w-3.5" /> Add field
          </button>
        </>
      )}
    </div>
  );
}
